// ============================================================
// canvasExport.js — flatten an edited photo to a <canvas> / Blob
// ------------------------------------------------------------
// Full pipeline (export only — live preview is CSS, see EditorCanvas):
//   1. CSS-safe primitives via ctx.filter (brightness/contrast/saturate/
//      grayscale/hue-rotate)
//   2. per-pixel tone ops: warmth, highlights, shadows, fade
//   3. sharpen / structure (3×3 unsharp convolution)
//   4. tilt-shift (blur top/bottom bands via a gradient mask)
//   5. vignette (radial-gradient overlay)
//   6. grain (per-pixel noise)
//   7. text layers (ctx.fillText with per-style backgrounds)
//
// Supabase public URLs allow CORS, so crossOrigin="anonymous" keeps the
// canvas untainted and toBlob() works (mirrors the carousel compositor).
// ============================================================

import { buildCSSFilterString } from './filterEngine';
import { TEXT_STYLES, textFillColor } from './textStyles';

const MAX_EXPORT_EDGE = 2048;
// No-background text blocks get a minimum width so left/right alignment has
// room to shift the line. Mirrors EditorCanvas.MIN_BLOCK_FRAC.
const MIN_TEXT_BLOCK_FRAC = 0.30;
const clamp = (v) => (v < 0 ? 0 : v > 255 ? 255 : v);
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image failed to load (possible CORS / bad URL)'));
    img.src = src;
  });
}

function exportDims(w, h) {
  const longEdge = Math.max(w, h);
  if (longEdge <= MAX_EXPORT_EDGE) return { w, h };
  const scale = MAX_EXPORT_EDGE / longEdge;
  return { w: Math.round(w * scale), h: Math.round(h * scale) };
}

// ── Per-pixel tone ops ──────────────────────────────────────────────────
function applyToneOps(imgData, s) {
  const d = imgData.data;
  const warmth = (s.warmth || 0) / 100;
  const hl = (s.highlights || 0) / 100;
  const sh = (s.shadows || 0) / 100;
  const fade = (s.fade || 0) / 100;
  if (!warmth && !hl && !sh && !fade) return;

  for (let i = 0; i < d.length; i += 4) {
    let r = d[i], g = d[i + 1], b = d[i + 2];

    // Warmth: + warms (R up, B down), - cools (B up, R down).
    if (warmth) {
      r = clamp(r + 28 * warmth);
      g = clamp(g + 6 * warmth);
      b = clamp(b - 28 * warmth);
    }

    const luma = 0.299 * r + 0.587 * g + 0.114 * b;

    // Highlights: act on bright pixels.
    if (hl) {
      const wgt = Math.max(0, (luma - 128) / 127);
      const adj = hl * 65 * wgt;
      r = clamp(r + adj); g = clamp(g + adj); b = clamp(b + adj);
    }
    // Shadows: act on dark pixels (+ lifts, - crushes).
    if (sh) {
      const wgt = Math.max(0, (128 - luma) / 128);
      const adj = sh * 65 * wgt;
      r = clamp(r + adj); g = clamp(g + adj); b = clamp(b + adj);
    }
    // Fade: lift blacks toward a matte grey.
    if (fade) {
      const lift = fade * 38;
      const k = 1 - fade * 0.10;
      r = clamp(r * k + lift); g = clamp(g * k + lift); b = clamp(b * k + lift);
    }

    d[i] = r; d[i + 1] = g; d[i + 2] = b;
  }
}

// ── Sharpen / structure (3×3 unsharp) ───────────────────────────────────
function applySharpen(ctx, w, h, s) {
  const amount = clamp01((s.sharpen || 0) / 100 * 0.9 + (s.structure || 0) / 100 * 0.6);
  if (amount <= 0) return;
  const src = ctx.getImageData(0, 0, w, h);
  const out = ctx.createImageData(w, h);
  const sd = src.data, od = out.data;
  const c = 1 + 4 * amount;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      if (x === 0 || y === 0 || x === w - 1 || y === h - 1) {
        od[i] = sd[i]; od[i + 1] = sd[i + 1]; od[i + 2] = sd[i + 2]; od[i + 3] = sd[i + 3];
        continue;
      }
      for (let ch = 0; ch < 3; ch++) {
        const v =
          sd[i + ch] * c -
          amount * (sd[i - 4 + ch] + sd[i + 4 + ch] + sd[i - w * 4 + ch] + sd[i + w * 4 + ch]);
        od[i + ch] = clamp(v);
      }
      od[i + 3] = sd[i + 3];
    }
  }
  ctx.putImageData(out, 0, 0);
}

// ── Tilt-shift (blur top/bottom bands) ──────────────────────────────────
function applyTiltShift(canvas, ctx, w, h, s) {
  const amt = (s.tiltShift || 0) / 100;
  if (amt <= 0) return;
  const radius = Math.round(2 + amt * 12);
  const blurred = document.createElement('canvas');
  blurred.width = w; blurred.height = h;
  const bctx = blurred.getContext('2d');
  bctx.filter = `blur(${radius}px)`;
  bctx.drawImage(canvas, 0, 0);
  bctx.filter = 'none';

  // Keep the blurred copy only in the top/bottom bands via a gradient mask.
  bctx.globalCompositeOperation = 'destination-in';
  const g = bctx.createLinearGradient(0, 0, 0, h);
  g.addColorStop(0.00, 'rgba(0,0,0,1)');
  g.addColorStop(0.22, 'rgba(0,0,0,1)');
  g.addColorStop(0.36, 'rgba(0,0,0,0)');
  g.addColorStop(0.64, 'rgba(0,0,0,0)');
  g.addColorStop(0.78, 'rgba(0,0,0,1)');
  g.addColorStop(1.00, 'rgba(0,0,0,1)');
  bctx.fillStyle = g;
  bctx.fillRect(0, 0, w, h);
  bctx.globalCompositeOperation = 'source-over';

  ctx.drawImage(blurred, 0, 0);
}

// ── Vignette ────────────────────────────────────────────────────────────
function applyVignette(ctx, w, h, s) {
  const v = (s.vignette || 0) / 100;
  if (v <= 0) return;
  const g = ctx.createRadialGradient(
    w / 2, h / 2, Math.min(w, h) * 0.32,
    w / 2, h / 2, Math.max(w, h) * 0.72,
  );
  g.addColorStop(0, 'rgba(0,0,0,0)');
  g.addColorStop(1, `rgba(0,0,0,${Math.min(0.85, v * 0.9)})`);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
}

// ── Grain ───────────────────────────────────────────────────────────────
function applyGrain(ctx, w, h, s) {
  const grain = (s.grain || 0) / 100;
  if (grain <= 0) return;
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const intensity = grain * 42;
  for (let i = 0; i < d.length; i += 4) {
    const n = (Math.random() - 0.5) * intensity;
    d[i] = clamp(d[i] + n);
    d[i + 1] = clamp(d[i + 1] + n);
    d[i + 2] = clamp(d[i + 2] + n);
  }
  ctx.putImageData(img, 0, 0);
}

// ── Rounded-rect helper (fallback for older canvas) ─────────────────────
function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

// ── Text layers ─────────────────────────────────────────────────────────
function drawTextLayers(ctx, w, h, layers) {
  if (!layers || layers.length === 0) return;
  for (const layer of layers) {
    const st = TEXT_STYLES[layer.style] || TEXT_STYLES.classic;
    const fontPx = Math.max(8, (layer.sizeFrac || 0.09) * h);
    const rawLines = String(layer.text || '').split('\n');
    const lines = rawLines.map((l) => (st.uppercase ? l.toUpperCase() : l));
    ctx.font = `${st.italic ? 'italic ' : ''}${st.weight} ${fontPx}px ${st.fontFamily}`;
    ctx.textBaseline = 'middle';

    const lineH = fontPx * 1.2;
    const totalH = lineH * lines.length;
    const cx = (layer.x ?? 0.5) * w;
    const topCy = (layer.y ?? 0.5) * h - totalH / 2 + lineH / 2;

    const maxW = lines.reduce((m, l) => Math.max(m, ctx.measureText(l).width), 0);

    // Background box (Editor pill / Bubble).
    if (st.bg === 'pill' || st.bg === 'bubble') {
      const padX = fontPx * 0.42;
      const padY = fontPx * 0.30;
      const bx = cx - maxW / 2 - padX;
      const by = topCy - lineH / 2 - padY;
      const bw = maxW + padX * 2;
      const bh = totalH + padY * 2;
      const a = clamp01((layer.opacity ?? 55) / 100);
      if (st.bg === 'pill') {
        ctx.fillStyle = `rgba(0,0,0,${a})`;
      } else {
        const { r, g, b } = hexToRgb(layer.color || '#31DCBA');
        ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
      }
      roundRect(ctx, bx, by, bw, bh, fontPx * 0.32);
      ctx.fill();
    }

    // Block width: no-bg styles get a min width so alignment can shift the line.
    const minBlock = st.bg === 'none' ? MIN_TEXT_BLOCK_FRAC * w : 0;
    const blockW = Math.min(0.9 * w, Math.max(maxW, minBlock));
    const blockLeft = cx - blockW / 2;
    let anchorX;
    if (layer.align === 'left') { ctx.textAlign = 'left'; anchorX = blockLeft; }
    else if (layer.align === 'right') { ctx.textAlign = 'right'; anchorX = blockLeft + blockW; }
    else { ctx.textAlign = 'center'; anchorX = cx; }

    // Shadow / glow setup per style.
    if (st.glow) {
      ctx.shadowColor = layer.color || '#5ffbf1';
      ctx.shadowBlur = fontPx * 0.5;
      ctx.shadowOffsetX = 0; ctx.shadowOffsetY = 0;
    } else if (st.dropShadow) {
      ctx.shadowColor = 'rgba(0,0,0,0.9)';
      ctx.shadowBlur = fontPx * 0.18;
      ctx.shadowOffsetX = fontPx * 0.05; ctx.shadowOffsetY = fontPx * 0.05;
    } else if (st.shadow) {
      ctx.shadowColor = 'rgba(0,0,0,0.55)';
      ctx.shadowBlur = fontPx * 0.16;
      ctx.shadowOffsetX = 0; ctx.shadowOffsetY = fontPx * 0.04;
    }

    if (st.outline) {
      // Transparent fill, solid stroke (matches -webkit-text-stroke preview).
      ctx.lineWidth = Math.max(1.5, fontPx * 0.045);
      ctx.strokeStyle = layer.color || '#ffffff';
      ctx.lineJoin = 'round';
      lines.forEach((line, i) => ctx.strokeText(line, anchorX, topCy + i * lineH));
    } else {
      ctx.fillStyle = textFillColor(st, layer);
      lines.forEach((line, i) => ctx.fillText(line, anchorX, topCy + i * lineH));
      // Neon: a second pass intensifies the glow.
      if (st.glow) lines.forEach((line, i) => ctx.fillText(line, anchorX, topCy + i * lineH));
    }

    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
  }
}

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  if (!m) return { r: 255, g: 255, b: 255 };
  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) };
}

// ── Main render ─────────────────────────────────────────────────────────
// `crop` (optional) is { x, y, width, height } as 0–1 fractions of the
// original image. It is the FIRST step: we sample only that source rect and
// size the canvas to the cropped region; every downstream op then runs on the
// cropped pixels exactly as before. Text layers keep their fractions relative
// to the ORIGINAL image (see the translate below), so anything outside the
// crop is simply clipped by the canvas.
export function renderToCanvas(imgEl, settings, textLayers = [], crop = null) {
  const natW = imgEl.naturalWidth || imgEl.width;
  const natH = imgEl.naturalHeight || imgEl.height;

  // Source rect in original-image pixels.
  const cx = crop ? clamp01(crop.x) : 0;
  const cy = crop ? clamp01(crop.y) : 0;
  const sx = cx * natW;
  const sy = cy * natH;
  const sw = Math.max(1, (crop ? clamp01(crop.width) : 1) * natW);
  const sh = Math.max(1, (crop ? clamp01(crop.height) : 1) * natH);

  // Output is sized to the cropped region (long edge still capped at 2048).
  const { w, h } = exportDims(sw, sh);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');

  // 1. CSS-safe primitives during draw — sample the crop rect into the canvas.
  ctx.filter = buildCSSFilterString(settings);
  ctx.drawImage(imgEl, sx, sy, sw, sh, 0, 0, w, h);
  ctx.filter = 'none';

  // 2. per-pixel tone ops.
  const toneData = ctx.getImageData(0, 0, w, h);
  applyToneOps(toneData, settings);
  ctx.putImageData(toneData, 0, 0);

  // 3. sharpen / structure.
  applySharpen(ctx, w, h, settings);
  // 4. tilt-shift.
  applyTiltShift(canvas, ctx, w, h, settings);
  // 5. vignette.
  applyVignette(ctx, w, h, settings);
  // 6. grain.
  applyGrain(ctx, w, h, settings);
  // 7. text — drawn in the ORIGINAL image's coordinate space, then offset so
  //    the crop rect maps to the canvas origin. When there's no crop this is a
  //    no-op translate over the full canvas (identical to the un-cropped path).
  if (textLayers && textLayers.length) {
    const scaleX = w / sw;
    const scaleY = h / sh;
    ctx.save();
    ctx.translate(-sx * scaleX, -sy * scaleY);
    drawTextLayers(ctx, natW * scaleX, natH * scaleY, textLayers);
    ctx.restore();
  }

  return canvas;
}

export function canvasToBlob(canvas, type = 'image/jpeg', quality = 0.92) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('canvas.toBlob returned null'))),
      type,
      quality,
    );
  });
}

export async function exportEdited(src, settings, textLayers = [], crop = null) {
  const img = await loadImage(src);
  const canvas = renderToCanvas(img, settings, textLayers, crop);
  return canvasToBlob(canvas);
}

// Lightweight CSS-only thumbnail (no pixel ops) for the filter strip — fast.
export function renderThumbnail(imgEl, settings, size = 60) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const natW = imgEl.naturalWidth || imgEl.width;
  const natH = imgEl.naturalHeight || imgEl.height;
  const side = Math.min(natW, natH);
  const sx = (natW - side) / 2;
  const sy = (natH - side) / 2;
  ctx.filter = buildCSSFilterString(settings);
  ctx.drawImage(imgEl, sx, sy, side, side, 0, 0, size, size);
  ctx.filter = 'none';
  return canvas.toDataURL('image/jpeg', 0.7);
}
